import { useEffect, useRef, useState } from 'react';
import {
  PiCameraDuotone,
  PiCursorClickDuotone,
  PiListNumbersDuotone,
  PiNoteDuotone,
  PiPolygonDuotone,
  PiRulerDuotone,
  PiSelectionAllDuotone,
} from 'react-icons/pi';

import { useTranslate } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import { AidTooltip, ButtonGroup, ButtonGroupProps, ButtonProps } from '@spazious/dollhouse-uikit';

import { useAidTooltipData, useDeviceInfo } from '../../hooks/index.js';
import { AnnotationToolControls } from '../AnnotationToolControls/index.js';
import { CamerapathToolControls } from '../CamerapathToolControls/index.js';
import { Features } from '../Features/index.js';
import { MeasurementToolControls } from '../MeasurementToolControls/index.js';

import { ToolModeToggleButton } from './ToolModeToggleButton.js';

export interface ToolModeButtonGroupProps extends ButtonGroupProps {}

export const ToolModeButtonGroup: React.FC<ToolModeButtonGroupProps> = (props) => {
  const getAidInfo = useAidTooltipData();
  const [showSelectBox, setShowSelectBox] = useState(false);
  const translate = useTranslate();
  const selectedTool = useStore(({ tool }) => tool);
  const controls = useStore(({ controls }) => controls);
  const showButtonTitles = useStore(({ settings }) => settings.showButtonTitles);
  const isMobile = useDeviceInfo((state) => state.isMobile);
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';
  const groupRef = useRef<Element>();

  useEffect(() => {
    const keyup = () => {
      setShowSelectBox(false);
      window.removeEventListener('keyup', keyup);
    };
    const keydown = (e: KeyboardEvent) => {
      if (e.shiftKey) {
        setShowSelectBox(true);
      }
      window.addEventListener('keyup', keyup);
    };
    const visibilitychange = () => {
      setShowSelectBox(false);
    };

    window.addEventListener('keydown', keydown);
    document.addEventListener('visibilitychange', visibilitychange);

    return () => {
      window.removeEventListener('keydown', keydown);
      window.removeEventListener('keyup', keyup);
      document.removeEventListener('visibilitychange', visibilitychange);
    };
  }, []);

  return (
    <ButtonGroup {...props} ref={groupRef as any}>
      <Features features="tool.camerapath">
        <ToolModeToggleButton
          controls={<CamerapathToolControls />}
          controlsPortal={groupRef.current || undefined}
          icon={<PiCameraDuotone />}
          label={translate('Cam-Path')}
          size={buttonSize}
          tool={ToolModeEnum.CameraPath}
        />
      </Features>
      <Features features="tool.farea">
        <ToolModeToggleButton
          icon={<PiPolygonDuotone />}
          label={translate('R-Area')}
          size={buttonSize}
          tool={ToolModeEnum.FArea}
        />
      </Features>
      <Features features="tool.draw">
        <AidTooltip {...getAidInfo('orbit-camera-controls')}>
          <ToolModeToggleButton
            icon={<PiPolygonDuotone />}
            label={translate('Draw')}
            size={buttonSize}
            tool={ToolModeEnum.Draw}
          />
        </AidTooltip>
      </Features>
      <Features features="tool.annotation">
        <AidTooltip {...getAidInfo('annotation')}>
          <ToolModeToggleButton
            controls={<AnnotationToolControls />}
            controlsPortal={groupRef.current || undefined}
            icon={<PiNoteDuotone />}
            label={translate('Annotation')}
            tool={ToolModeEnum.Annotation}
          />
        </AidTooltip>
      </Features>
      <Features features="tool.ruler">
        <AidTooltip {...getAidInfo('ruler')}>
          <ToolModeToggleButton
            controls={<MeasurementToolControls />}
            controlsPortal={groupRef.current || undefined}
            icon={<PiRulerDuotone />}
            label={translate('Measurement')}
            minWidth={showButtonTitles ? '90px' : ''}
            size={buttonSize}
            tool={ToolModeEnum.Ruler}
          />
        </AidTooltip>
      </Features>

      <Features features="tool.select">
        <AidTooltip {...getAidInfo('select')}>
          <ToolModeToggleButton
            hint={`Press 'Shift' to activate selection box tool`}
            icon={
              selectedTool === ToolModeEnum.Select && showSelectBox ? (
                <PiSelectionAllDuotone />
              ) : (
                <PiCursorClickDuotone />
              )
            }
            label={translate('Multiselect')}
            size={buttonSize}
            tool={ToolModeEnum.Select}
          />
        </AidTooltip>
      </Features>
      <Features features="tool.numbering">
        <AidTooltip {...getAidInfo('numbering')}>
          <ToolModeToggleButton
            disabled={controls !== ControlsModeEnum.Zenithal}
            icon={<PiListNumbersDuotone />}
            label={translate('Numbering')}
            size={buttonSize}
            tool={ToolModeEnum.Numbering}
          />
        </AidTooltip>
      </Features>
    </ButtonGroup>
  );
};
